import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { useToast } from '@givebutter/primitives/toast';
import { updateWorkflow } from 'api/workflows';
import { enabledTriggers } from 'assets/js/pages/engage/workflows/utils/starterItems';
import * as Form from 'components/form';
import * as Tooltip from 'components/tooltip';
import { WorkflowTriggerTypeEnum } from 'constants/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { queryClient } from 'queries/queryClient';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { Workflow } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ExecuteOnceFormValues {
    execute_once: string;
}
const TriggerExecuteOnceSetting = () => {
    const [isEditing, setIsEditing] = useState(false);
    const { error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();
    const trigger = enabledTriggers.find(t => t.slug === workflow.trigger);

    const methods = Form.useGivebutterForm<ExecuteOnceFormValues>({
        defaultValues: {
            execute_once: workflow.execute_once.toString(),
        },
    });
    const { handleSubmit, reset, path, control } = methods;

    const { mutate: submit, isPending } = useMutation<
        Workflow,
        GivebutterAxiosError,
        ExecuteOnceFormValues
    >({
        mutationFn: values =>
            updateWorkflow(accountId, workflow.id, {
                execute_once: values.execute_once === 'true',
                name: workflow.name,
            }),
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: workflowQueryKeys.workflows(accountId) });
            queryClient.setQueryData(
                workflowQueryKeys.workflowById(accountId, response.id),
                response,
            );

            reset({ execute_once: response.execute_once.toString() });
            setIsEditing(false);
        },
        onError: e => {
            error(e.response?.data.message || 'Failed to update workflow. Please try again.');
        },
    });

    if (!trigger) return null;
    const { enabled: reEnrollmentEnabled, initialValue } = trigger.reEnrollment || {};

    if (!reEnrollmentEnabled) {
        return (
            <div className="flex flex-col gap-2 px-6 py-4">
                <p className="text-sm font-medium text-gray-900">Re-enter workflow?</p>
                <p>Can’t be configured because your trigger is a one-time event.</p>
            </div>
        );
    }
    return (
        <Form.Provider {...methods}>
            <form
                onSubmit={handleSubmit(values => submit(values))}
                className="flex flex-col gap-4 px-6 py-4"
            >
                <div className="flex items-center justify-between">
                    <div className="flex w-full flex-col gap-2">
                        <p className="text-sm font-medium text-gray-900">Re-enter workflow?</p>

                        {isEditing ? (
                            <div className="flex w-full justify-between">
                                <Form.UseFormRadioGroup
                                    className="flex w-fit flex-col gap-2"
                                    name="execute_once"
                                    control={control}
                                >
                                    <RadioGroup.ItemContainer>
                                        <RadioGroup.Item id="execute_once_true" value="true">
                                            <RadioGroup.Indicator />
                                        </RadioGroup.Item>
                                        <Form.Label
                                            name={path('execute_once')}
                                            className="flex cursor-pointer gap-2 font-normal leading-none"
                                            htmlFor="execute_once_true"
                                        >
                                            <p>
                                                Supporters <strong>can’t</strong> enter this
                                                workflow more than once
                                            </p>
                                            {initialValue === true && (
                                                <RecommendedBadge trigger={workflow.trigger} />
                                            )}
                                        </Form.Label>
                                    </RadioGroup.ItemContainer>
                                    <RadioGroup.ItemContainer>
                                        <RadioGroup.Item id="execute_once_false" value="false">
                                            <RadioGroup.Indicator />
                                        </RadioGroup.Item>
                                        <Form.Label
                                            name={path('execute_once')}
                                            className="flex cursor-pointer gap-2 font-normal leading-none"
                                            htmlFor="execute_once_false"
                                        >
                                            <p>Supporters can enter this workflow more than once</p>
                                            {initialValue === false && (
                                                <RecommendedBadge trigger={workflow.trigger} />
                                            )}
                                        </Form.Label>
                                    </RadioGroup.ItemContainer>
                                </Form.UseFormRadioGroup>

                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        color="neutral"
                                        onClick={() => {
                                            setIsEditing(false);
                                            reset();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Form.SubmitButton size="sm" isSubmitting={isPending}>
                                        Save
                                    </Form.SubmitButton>
                                </div>
                            </div>
                        ) : (
                            <div className="flex gap-1">
                                <Badge color={workflow.execute_once ? 'error' : 'success'}>
                                    {workflow.execute_once ? 'NO' : 'YES'}
                                </Badge>
                                <p>
                                    Supporters {workflow.execute_once ? 'can’t' : 'can'} enter this
                                    workflow multiple times.
                                </p>
                            </div>
                        )}
                    </div>
                    {!isEditing && reEnrollmentEnabled && (
                        <Button size="sm" variant="transparent" onClick={() => setIsEditing(true)}>
                            Edit
                        </Button>
                    )}
                </div>
                {!isEditing && reEnrollmentEnabled && (
                    <div className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                        <div className="text-sm font-medium">💡 Tip</div>
                        <p className="text-xs text-gray-600">
                            <CalloutTipText trigger={workflow.trigger} />
                        </p>
                    </div>
                )}
            </form>
        </Form.Provider>
    );
};

interface CalloutTipTextProps {
    trigger: WorkflowTriggerTypeEnum;
}
const CalloutTipText = ({ trigger }: CalloutTipTextProps) => {
    switch (trigger) {
        case WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION:
            return '';
        case WorkflowTriggerTypeEnum.DONATION_CREATED:
            return 'A supporter can make many donations that match your trigger criteria. If you’re sending an email, thanking supporters for their gift every single time goes a long way. To do this, allow supporters to re-enter your workflow.';
        case WorkflowTriggerTypeEnum.CONTACT_TOTAL_CONTRIBUTIONS:
            return '';
        case WorkflowTriggerTypeEnum.CONTACT_TAGGED:
            return 'Adding and removing tags from a contact may unintentionally trigger the workflow when you allow supporters to enter the workflow multiple times. We recommend only allowing supporters to enter the workflow once to prevent this.';
        case WorkflowTriggerTypeEnum.PLAN_CREATED:
            return 'A supporter can become a recurring donor more than once. It’s worth showing appreciation every time donors make their first recurring donation. To do this, allow supporters to re-enter your workflow.';
        case WorkflowTriggerTypeEnum.PLAN_ANNIVERSARY:
            return 'Celebrating a supporter’s recurring plan anniversary will help you show appreciation and steward the supporter, which can increase their retention. To do this, allow supporters to re-enter your workflow.';
        case WorkflowTriggerTypeEnum.PLAN_CANCELED:
            return 'Reaching out to a supporter every time they cancel their recurring plan will help you understand their reason and identify potential reactivation opportunities. Sometimes, the cancellation is temporary or a mistake! To do this, allow supporters to re-enter your workflow.';
        case WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM:
            return 'Reaching out to a supporter every time they don’t complete their donation can help you recapture donations. To do this, allow supporters to re-enter your workflow.';
        case WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION:
            return 'Reaching out often to supporters who haven’t donated in a while can not only increase your donations but also increase donor retention. To do this, allow supporters to re-enter your workflow.';
        case WorkflowTriggerTypeEnum.TEAM_MEMBER_MILESTONE:
            return 'If you are congratulating members for reaching a milestone for any campaign, having them re-enter your workflow may be beneficial if they are fundraising for multiple. If it’s for only one campaign, re-entering will not apply.';
        case WorkflowTriggerTypeEnum.TICKET_CREATED:
            return 'Since supporters can purchase multiple tickets for multiple campaigns, allowing them to re-enter the workflow would be beneficial if you want them to receive communication from you more than once.';
        case WorkflowTriggerTypeEnum.CONTACT_BIRTHDAY:
            return 'If you’re sending a message to celebrate a supporter’s birthday, make sure you allow supporters to re-enter the workflow so they can get a message from your organization every year!';
        case WorkflowTriggerTypeEnum.ATTENDED_EVENT:
            return 'If you’re sending a message to communicate with supporters after an event, make sure you allow supporters to re-enter the workflow so they can get a message from your organization every time they attend an event!';
        case WorkflowTriggerTypeEnum.DID_NOT_WIN_AUCTION:
            return 'If you’re sending a message to supporters after the auction, make sure you allow supporters to re-enter the workflow so they can get a message from your organization every time they participate in your auctions!';
        default:
            return '';
    }
};

interface ToolTipTextProps {
    trigger: WorkflowTriggerTypeEnum;
}
const ToolTipText = ({ trigger }: ToolTipTextProps) => {
    switch (trigger) {
        case WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION:
            return '';
        case WorkflowTriggerTypeEnum.DONATION_CREATED:
            return 'If you’re sending an email to a supporter making a large donation, thanking them for their gift every single time goes a long way.';
        case WorkflowTriggerTypeEnum.CONTACT_TOTAL_CONTRIBUTIONS:
            return '';
        case WorkflowTriggerTypeEnum.CONTACT_TAGGED:
            return 'Adding and removing tags from a contact may unintentionally trigger the workflow.';
        case WorkflowTriggerTypeEnum.PLAN_CREATED:
            return 'If you’re sending an email to thank donors, it’s worth showing appreciation every time they make their first recurring donation.';
        case WorkflowTriggerTypeEnum.PLAN_ANNIVERSARY:
            return 'Celebrating a supporter’s recurring plan anniversary will help you steward the donor and increase donor retention.';
        case WorkflowTriggerTypeEnum.PLAN_CANCELED:
            return 'Reaching out to a supporter every time they cancel their recurring plan will help you understand their reason and identify potential reactivation opportunities.';
        case WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM:
            return 'Reaching out to a supporter when they don’t complete their donation every time can help you recapture donations. ';
        case WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION:
            return 'Reaching out often to supporters who haven’t donated in a while can not only increase your donations but also increase donor retention.';
        case WorkflowTriggerTypeEnum.TEAM_MEMBER_MILESTONE:
            return 'Having team members re-enter your workflow may be beneficial if they are fundraising for multiple campaigns.';
        case WorkflowTriggerTypeEnum.TICKET_CREATED:
            return 'Since supporters can purchase multiple tickets for multiple campaigns, allowing them to re-enter will be beneficial if you want them to receive communication from you more than once.';
        case WorkflowTriggerTypeEnum.CONTACT_BIRTHDAY:
            return 'Make sure your supporters get a message from your organization every year!';
        case WorkflowTriggerTypeEnum.ATTENDED_EVENT:
            return 'Make sure your supporters get a message from your organization every time they attend an event!';
        case WorkflowTriggerTypeEnum.DID_NOT_WIN_AUCTION:
            return 'Make sure your supporters get a message from your organization every time they participate in your auctions!';
        default:
            return '';
    }
};

interface RecommendedBadgeProps {
    trigger: WorkflowTriggerTypeEnum;
}
const RecommendedBadge = ({ trigger }: RecommendedBadgeProps) => {
    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <Badge color="primary" size="sm">
                    Recommended
                </Badge>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content inverse>
                    <p>
                        <ToolTipText trigger={trigger} />
                    </p>
                    <Tooltip.Arrow inverse />
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
};

export { TriggerExecuteOnceSetting };
